import MakerJS, { type exporter, type IModel, type IModelMap } from "makerjs";
import * as opentype from "opentype.js";

enum FontFamily {
  NOTO = "NOTO",
}

const fontsToLoad: { [key in FontFamily]: string } = {
  [FontFamily.NOTO]: "/fonts/NotoMono-Regular.ttf",
};

const fonts: { [key in FontFamily]: opentype.Font | null } = {
  [FontFamily.NOTO]: null,
};

for (const fontName of Object.keys(fontsToLoad) as FontFamily[]) {
  opentype.load(fontsToLoad[fontName], (err, loadedFont) => {
    if (err) {
      console.error(err);
    } else if (loadedFont) {
      fonts[fontName] = loadedFont;
    }
  });
}

export const DefaultLayerName = "default";

export class MakerJSModel implements IModel {
  public static readonly defaultDXFRenderOptions: exporter.IDXFRenderOptions = {
    fontSize: 2,
    units: "mm",
    usePOLYLINE: true,
  };

  public static readonly defaultSVGRenderOptions: exporter.ISVGRenderOptions = {
    svgAttrs: { xmlns: "http://www.w3.org/2000/svg" },
    units: "mm",
  };

  public models: IModelMap = {};

  public readonly dxfRenderOptions: exporter.IDXFRenderOptions;
  public readonly svgRenderOptions: exporter.ISVGRenderOptions;

  private nextId = 1;

  constructor(options?: {
    dxfRenderOptions?: exporter.IDXFRenderOptions;
    svgRenderOptions?: exporter.ISVGRenderOptions;
  }) {
    this.dxfRenderOptions = MakerJSModel.defaultDXFRenderOptions;
    this.svgRenderOptions = MakerJSModel.defaultSVGRenderOptions;

    if (options) {
      if (options.dxfRenderOptions) {
        this.dxfRenderOptions = {
          ...MakerJSModel.defaultDXFRenderOptions,
          ...options.dxfRenderOptions,
        };
      }
      if (options.svgRenderOptions) {
        this.svgRenderOptions = {
          ...MakerJSModel.defaultSVGRenderOptions,
          ...options.svgRenderOptions,
        };
      }
    }
  }

  public addRect(
    x: number,
    y: number,
    w: number,
    h: number,
    layer: string = DefaultLayerName,
  ) {
    const model = MakerJS.model.move(new MakerJS.models.Rectangle(w, h), [
      x,
      y,
    ]);
    model.layer = layer;
    this.models[this.nextId++] = model;
  }
  public addLine(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    layer: string = DefaultLayerName,
    caption?: string,
    captionOutset = 1.1,
  ) {
    const model = {
      layer,
      paths: {
        line1: new MakerJS.paths.Line([x1, y1], [x2, y2]),
      },
    };
    const coX = x1 !== x2 ? 1 : captionOutset;
    const coY = y1 !== y2 ? 1 : captionOutset;
    if (caption) {
      if (x1 + y1 < x2 + y2) {
        this.models[this.nextId++] = MakerJS.model.addCaption(
          model,
          caption,
          x1 === x2 ? [x1 * coX, y1 * coY] : [x1 * coX, y1 * coY],
          x1 === x2 ? [x2 * coX, y2 * coY] : [x2 * coX, y2 * coY],
        );
      } else {
        this.models[this.nextId++] = MakerJS.model.addCaption(
          model,
          caption,
          x1 === x2 ? [x2 * coX, y2 * coY] : [x2 * coX, y2 * coY],
          x1 === x2 ? [x1 * coX, y1 * coY] : [x1 * coX, y1 * coY],
        );
      }
    } else {
      this.models[this.nextId++] = model;
    }
  }
  public addText(
    text: string,
    x: number,
    y: number,
    _layer: string = DefaultLayerName,
    size = 20,
    orientation = 0,
    fontFamily: FontFamily = FontFamily.NOTO,
  ) {
    const selectedFont = fonts[fontFamily];
    if (!selectedFont) {
      throw new Error(`Font ${fontFamily} not loaded`);
    }

    const model = MakerJS.model.move(
      MakerJS.model.rotate(
        new MakerJS.models.Text(selectedFont, text, size, true, false),
        orientation,
      ),
      [x, y],
    );
    this.models[this.nextId++] = model;
  }
  public addCaption(text: string, x: number, y: number, layer?: string) {
    const m: IModel = {};
    if (layer) {
      m.layer = layer;
    }
    MakerJS.model.addCaption(m, `${text}`, [x - 30, y], [x + 30, y]);
    this.models[this.nextId++] = m;
  }
  public addTextOnLine(
    text: string,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    layer: string = DefaultLayerName,
    size = 20,
    fontFamily: FontFamily = FontFamily.NOTO,
  ): void {
    const path = new MakerJS.paths.Line([x1, y1], [x2, y2]);
    const currentFont = fonts[fontFamily];
    if (!currentFont) {
      throw new Error(`Font ${fontFamily} not loaded`);
    }
    const textModel = new MakerJS.models.Text(
      currentFont,
      text,
      size,
      true,
      false,
    );
    const model = MakerJS.layout.childrenOnPath(textModel, path, -0.1);
    model.layer = layer;
    this.models[this.nextId++] = model;
  }
  public addSubModel(
    subModel: IModel,
    offsetX: number,
    offsetY: number,
    layer: string = DefaultLayerName,
    scale = 1,
  ): void {
    // Check if the model has any measurable content before centering
    // MakerJS.model.center() fails with "m is undefined" on empty models
    const extents = MakerJS.measure.modelExtents(subModel);
    if (!extents) {
      // Empty model, nothing to add
      return;
    }

    const subModelScaled = MakerJS.model.scale(subModel, scale);
    const subModelScaledCentered = MakerJS.model.center(subModelScaled);
    const subModelMovedScaledCentered = MakerJS.model.moveRelative(
      subModelScaledCentered,
      [offsetX, offsetY],
    );

    const model = {
      layer,
      models: {
        model1: subModelMovedScaledCentered,
      },
    };
    this.models[this.nextId++] = model;
  }

  public toSVG(): string {
    const svg = MakerJS.exporter.toSVG(this, this.svgRenderOptions);
    return svg;
  }

  public toDXF(): string {
    const dxf = MakerJS.exporter.toDXF(this, this.dxfRenderOptions);
    return dxf;
  }
}
